/**
 * Autoplay-with-sound policy handling for video slides (SCN-612).
 *
 * Browsers won't autoplay video *with sound* until the page has a user gesture / media engagement.
 * A kiosk display normally has neither, so a `muted:false` slide plays muted. This module tracks
 * that state so the behaviour is consistent (no flicker of a failed unmuted attempt on every
 * rotation) and recovers automatically:
 *
 *  - the first time the browser blocks unmuted playback we remember it, and later `muted:false`
 *    slides start muted straight away;
 *  - the first real user interaction (someone taps the screen) lifts that — future slides try
 *    sound again, and any slide currently playing is told to unmute via `onSoundAllowed`.
 *
 * For guaranteed sound with no interaction, launch the kiosk browser with
 * `--autoplay-policy=no-user-gesture-required` (Chrome/Chromium) or the platform equivalent.
 */
let unmutedAutoplayBlocked = false;
let userHasInteracted = false;
const subscribers = new Set<() => void>();

/** Whether a video slide should still attempt to play with sound this session. */
export const canTrySound = () => !unmutedAutoplayBlocked;

/** Called by a slide when the browser refuses unmuted playback. Sticky only until a user gesture. */
export const markUnmutedAutoplayBlocked = () => {
  if (!userHasInteracted) unmutedAutoplayBlocked = true;
};

/** Subscribe to "a user gesture just happened, sound is allowed now" — returns an unsubscribe. */
export const onSoundAllowed = (cb: () => void) => {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
};

function handleGesture() {
  userHasInteracted = true;
  unmutedAutoplayBlocked = false;
  subscribers.forEach((cb) => cb());
}

if (typeof window !== 'undefined') {
  for (const evt of ['pointerdown', 'keydown', 'touchstart'] as const) {
    window.addEventListener(evt, handleGesture, { passive: true });
  }
}

/** test-only reset. */
export const __resetAutoplayState = () => {
  unmutedAutoplayBlocked = false;
  userHasInteracted = false;
  subscribers.clear();
};
